import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsRotate,
  faCalendarDays,
  faFolderOpen,
  faLanguage,
  faPlay,
} from '@fortawesome/free-solid-svg-icons';
import DrawerWrapper from '@/app/common/components/DrawerWrapper';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import IconItem from '@/components/common/icon/IconItem';
import { trackerAiClient as api } from '@/app/TrackerAi/api/trackerAiClient';
import { useProject } from '../../context/ProjectContext';
import { useTrackerAiPath } from '../../utils/paths';
import { buildDateRangeQueryOptions } from '../../utils/visibility-range';
import { BrandsTable } from '../BrandsTable';
import { CompetitorModal } from '../Modals';
import { normalizeHost } from '../../utils/favicon';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { SectionTitle } from '../help';
import { getHelpHtml } from '../../utils/help/help-content';
import { StatusBadge } from '../MetricsCards';
import { ModelIcon } from '../ModelIcon';
import { ModelSwitch } from './ModelSwitch';
import { FilterChip, FilterMenuItem, ModelIcon as GenericModelIcon } from '../filters/FilterChip';
import { DateRangeFilter } from '../filters/DateRangeFilter';
import { TrackerAiTimePicker } from '../filters/TrackerAiTimePicker';
import { timeAgo } from '../../utils/format';
import { AiResponseText } from './AiResponseText';
import ClusterViewModeSwitcher from '@/app/KeywordResearch/components/keyword-clustring/components/ClusterViewModeSwitcher';
import { ProviderComparison } from './ProviderComparison';
import { PromptHistoryPanel } from './PromptHistoryPanel';
import { TopBrandAvatars } from './TopBrandAvatars';
import { PromptSourcesPanel } from '../sources/PromptSourcesPanel';
import { DuplicateExecutionNotice } from '../executions/DuplicateExecutionNotice';
import { PromptOverviewPanel } from './PromptOverviewPanel';
import { PromptModelResponseSkeleton } from '../common/Skeleton';
import { resolveInsightRelationship } from '../../utils/brand-relationship';
import type {
  DuplicateExecutionData,
  PromptOverview,
  PromptProviderResponse,
  PromptListTopBrand,
  UserBrandInsight,
  UserPromptInsight,
} from '../../types';

type PromptDetail = UserPromptInsight & {
  topicName?: string;
  providerResponses?: PromptProviderResponse[];
};

type InspectorTab = 'overview' | 'sources' | 'responses' | 'history';

const INSPECTOR_TABS = [
  { id: 'overview', label: 'نمای کلی', icon: 'bar-chart' },
  { id: 'sources', label: 'منابع', icon: 'database' },
  { id: 'responses', label: 'پاسخ مدل‌ها', icon: 'file-text' },
  { id: 'history', label: 'تاریخچه', icon: 'arrow-counterclockwise' },
] as const;

export function PromptInspector({
  prompt,
  topicName,
  running,
  duplicateExecution,
  onViewDuplicateResult,
  onRun,
  onClose,
  projectId,
  onAddCompetitor,
  onCreateCompetitor,
  brandRows,
}: {
  prompt: UserPromptInsight & { id: string };
  topicName?: string;
  brandRows: PromptListTopBrand[];
  running: boolean;
  duplicateExecution?: DuplicateExecutionData | null;
  onViewDuplicateResult?: (executionId: string) => void;
  onRun: () => void;
  onClose: () => void;
  projectId?: string;
  onAddCompetitor?: (brand: UserBrandInsight) => Promise<void>;
  onCreateCompetitor?: (data: {
    brandName: string;
    websiteUrl: string;
    aliases: string[];
  }) => Promise<void>;
}) {
  const [detail, setDetail] = useState<PromptDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const [comparisonRefreshKey, setComparisonRefreshKey] = useState(0);
  const [inspectorTab, setInspectorTab] = useState<InspectorTab>('overview');
  const [overview, setOverview] = useState<PromptOverview | null>(null);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [overviewError, setOverviewError] = useState(false);
  const [overviewModelId, setOverviewModelId] = useState<string | null>(null);
  const [competitorModalOpen, setCompetitorModalOpen] = useState(false);
  const [competitorModalBrand, setCompetitorModalBrand] = useState<UserBrandInsight | null>(
    null,
  );
  const [savingCompetitor, setSavingCompetitor] = useState(false);
  const navigate = useNavigate();
  const toTrackerAi = useTrackerAiPath();
  const { visibilityRange, customFrom, customTo, modelFilterId } = useProject();

  const rangeQueryOptions = useMemo(
    () =>
      buildDateRangeQueryOptions(visibilityRange, customFrom, customTo, {
        providerIds: modelFilterId ? [modelFilterId] : undefined,
      }),
    [visibilityRange, customFrom, customTo, modelFilterId],
  );

  const overviewRangeQueryOptions = useMemo(
    () =>
      buildDateRangeQueryOptions(visibilityRange, customFrom, customTo, {
        providerIds: overviewModelId
          ? [overviewModelId]
          : modelFilterId
            ? [modelFilterId]
            : undefined,
      }),
    [visibilityRange, customFrom, customTo, modelFilterId, overviewModelId],
  );

  useEffect(() => {
    let cancelled = false;
    setLoadingDetail(true);
    void loadDetail(cancelled);
    return () => {
      cancelled = true;
    };

    async function loadDetail(isCancelled: boolean) {
      try {
        const data = await api.getPromptInsight(prompt.id, rangeQueryOptions);
        if (isCancelled) return;
        setDetail({ ...data, topicName: data.topicName ?? topicName });
        const responses = data.providerResponses ?? [];
        setSelectedProviderId((prev) => {
          if (prev && responses.some((r) => r.modelId === prev)) return prev;
          return (
            responses.find((r) => r.status === 'completed')?.modelId ??
            responses[0]?.modelId ??
            data.modelId ??
            null
          );
        });
      } catch {
        if (!isCancelled) {
          setDetail({ ...prompt, topicName });
          setSelectedProviderId(prompt.modelId);
        }
      } finally {
        if (!isCancelled) setLoadingDetail(false);
      }
    }
  }, [prompt.id, topicName, rangeQueryOptions, prompt.status, prompt.lastRunAt, prompt.runs]);

  useEffect(() => {
    setComparisonRefreshKey((k) => k + 1);
  }, [prompt.id, prompt.status, prompt.lastRunAt, prompt.runs]);

  const wasRunningRef = useRef(running);
  useEffect(() => {
    if (wasRunningRef.current && !running) {
      setComparisonRefreshKey((k) => k + 1);
    }
    wasRunningRef.current = running;
  }, [running]);

  useEffect(() => {
    setInspectorTab('overview');
    setOverviewModelId(null);
  }, [prompt.id]);

  useEffect(() => {
    let cancelled = false;
    setLoadingOverview(true);

    void api
      .getPromptOverview(prompt.id, overviewRangeQueryOptions)
      .then((data) => {
        if (!cancelled) {
          setOverview(data);
          setOverviewError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOverviewError(true);
          setOverview(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingOverview(false);
      });

    return () => {
      cancelled = true;
    };
  }, [prompt.id, overviewRangeQueryOptions, comparisonRefreshKey]);

  async function handleBrandsTableAdd(brand: UserBrandInsight) {
    const host = brand.detectedHost ? normalizeHost(brand.detectedHost) : '';
    if (host && onAddCompetitor) {
      await onAddCompetitor({ ...brand, detectedHost: host });
      return;
    }
    if (!onCreateCompetitor) return;
    setCompetitorModalBrand(brand);
    setCompetitorModalOpen(true);
  }

  async function handleCompetitorModalSubmit(data: {
    brandName: string;
    websiteUrl: string;
    aliases: string[];
  }) {
    if (!onCreateCompetitor) return;
    setSavingCompetitor(true);
    try {
      await onCreateCompetitor(data);
      setCompetitorModalOpen(false);
      setCompetitorModalBrand(null);
    } finally {
      setSavingCompetitor(false);
    }
  }

  const wasRunning = useRef(false);
  useEffect(() => {
    if (wasRunning.current && !running) {
      void api.getPromptInsight(prompt.id, rangeQueryOptions).then((data) => {
        setDetail({ ...data, topicName: data.topicName ?? topicName });
        const responses = data.providerResponses ?? [];
        setSelectedProviderId((prev) => {
          if (prev && responses.some((r) => r.modelId === prev)) return prev;
          return (
            responses.find((r) => r.status === 'completed')?.modelId ??
            responses[0]?.modelId ??
            null
          );
        });
        setComparisonRefreshKey((key) => key + 1);
      });
    }
    wasRunning.current = running;
  }, [running, prompt.id, topicName, rangeQueryOptions]);

  const display = detail ?? prompt;
  const providerResponses = detail?.providerResponses ?? [];
  const overviewModelOptions = useMemo(() => {
    const seen = new Map<string, PromptProviderResponse>();
    for (const response of providerResponses) {
      if (!seen.has(response.modelId)) seen.set(response.modelId, response);
    }
    return [...seen.values()];
  }, [providerResponses]);
  const activeProvider =
    providerResponses.find((r) => r.modelId === selectedProviderId) ??
    providerResponses[0] ??
    null;

  const responseText = activeProvider?.responseText ?? display.responseText;
  const activeBrands = activeProvider?.brands ?? display.brands;
  const activeExecutedAt = activeProvider?.executedAt ?? display.executedAt;
  const hasResponse = Boolean(responseText);

  const topDetectedBrands = useMemo((): PromptListTopBrand[] => {
    return [...activeBrands]
      .sort((a, b) => {
        if (a.position != null && b.position != null) return a.position - b.position;
        return b.visibilityContribution - a.visibilityContribution;
      })
      .map(b => ({
        knownBrandId: b.knownBrandId ?? null,
        competitorId: b.competitorId ?? null,
        canonicalName: b.canonicalName ?? b.brandName,
        domain: b.detectedHost ?? null,
        faviconUrl: null,
        relationship: resolveInsightRelationship(b),
        visibilityScore: b.visibilityContribution,
        avgPosition: b.position,
        aiMentions: b.mentions,
        coverage: '',
      }));
  }, [activeBrands]);

  const activeModelLabel = activeProvider?.publicName ?? display.model ?? 'مدل';
  const showModelResponse =
    providerResponses.length > 0 || hasResponse || activeBrands.length > 0;

  function viewExecution(executionId: string) {
    void navigate(toTrackerAi('executions', `id=${encodeURIComponent(executionId)}`));
  }

  const promptTopic = detail?.topicName ?? topicName;
  const runsCount = overview?.runsCount ?? display.runsCount ?? 0;
  const lastRunAt = overview?.lastRunAt ?? display.executedAt;
  const languageLabel = overview?.languageLabel;

  const drawerTitle = (
    <div
      className="d-grid w-100 min-w-0 align-items-center gap-3 pe-2"
      style={{ gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)' }}
    >
      <div className="min-w-0">
        <SectionTitle
          title="جزئیات پرامپت"
          helpKey="prompt_inspector"
        />
      </div>
      <div className="d-inline-flex justify-content-center">
        <ClusterViewModeSwitcher
          value={inspectorTab}
          onChange={id => setInspectorTab(id as InspectorTab)}
          aria-label="بخش‌های جزئیات پرامپت"
          modes={INSPECTOR_TABS.map(tab => ({
            id: tab.id,
            label: tab.label,
            icon: tab.icon,
          }))}
        />
      </div>
      <div aria-hidden />
    </div>
  );

  const runButton = (
    <button
      type="button"
      className="btn btn-link link-secondary text-decoration-none shadow-none p-0 fs--1 d-inline-flex align-items-center gap-1 fw-semibold flex-shrink-0"
      onClick={onRun}
      disabled={running}
      title="اجرای پرامپت"
      aria-label="اجرای پرامپت"
    >
      {running ? (
        <Spinner animation="border" size="sm" />
      ) : (
        <FontAwesomeIcon icon={faPlay} className="fs--2" />
      )}
      {running ? 'در حال اجرا' : 'اجرا'}
    </button>
  );

  return (
    <>
      <DrawerWrapper show title={drawerTitle} onHide={onClose} width={1080}>
        <div className="mt-3">
          <div className="d-flex align-items-center justify-content-start gap-3 py-2">
          {inspectorTab === 'overview' && overviewModelOptions.length > 1 ? (
            <FilterChip
              icon={
                overviewModelId ? (
                  <ModelIcon
                    svg={overviewModelOptions.find(m => m.modelId === overviewModelId)?.iconSvg}
                    name={overviewModelOptions.find(m => m.modelId === overviewModelId)?.publicName ?? ''}
                    size={14}
                  />
                ) : (
                  <GenericModelIcon />
                )
              }
              label={
                overviewModelOptions.find(m => m.modelId === overviewModelId)?.publicName ??
                'همه مدل‌ها'
              }
              active={overviewModelId != null}
            >
              <FilterMenuItem selected={!overviewModelId} onClick={() => setOverviewModelId(null)}>
                همه مدل‌ها
              </FilterMenuItem>
              {overviewModelOptions.map(model => (
                <FilterMenuItem
                  key={model.modelId}
                  selected={overviewModelId === model.modelId}
                  onClick={() => setOverviewModelId(model.modelId)}
                  leading={<ModelIcon svg={model.iconSvg} name={model.publicName} size={18} />}
                >
                  {model.publicName}
                </FilterMenuItem>
              ))}
            </FilterChip>
          ) : null}
            {runButton}
            <div className="d-flex flex-wrap align-items-center gap-2 ms-auto">
              <DateRangeFilter />
              <TrackerAiTimePicker />
            </div>
          </div>
        </div>

        <div className="d-flex flex-column gap-3">
          <Card className="shadow-sm overflow-hidden border-200">
            <Card.Body className="p-0">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 pt-3 pb-2">
                <div className="d-flex flex-wrap align-items-center gap-2">
                  {promptTopic ? (
                    <span className="badge badge-subtle-secondary rounded-pill fs--2 fw-normal d-inline-flex align-items-center gap-1">
                      <FontAwesomeIcon icon={faFolderOpen} transform="shrink-2" />
                      {promptTopic}
                    </span>
                  ) : null}
                </div>
                <StatusBadge status={running ? 'running' : display.status} fullWidth={false} />
              </div>
              <div className="px-3 pb-3">
                <p className="fs-0 fw-medium text-900 mb-0 lh-base rounded-3 border border-200 bg-100 dark__bg-1100 px-3 py-3">
                  {display.text}
                </p>
              </div>
              <div className="border-top border-200 px-3 py-2 d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div className="d-flex flex-wrap align-items-center column-gap-3 row-gap-1">
                  <span className="d-inline-flex align-items-center gap-1 fs--1 text-600">
                    <FontAwesomeIcon icon={faArrowsRotate} className="text-400" transform="shrink-1" />
                    <span>
                      <span className="fw-semibold text-800">{runsCount.toLocaleString('fa-IR')}</span>{' '}
                      اجرا
                    </span>
                  </span>
                  {lastRunAt ? (
                    <span className="d-inline-flex align-items-center gap-1 fs--1 text-600">
                      <FontAwesomeIcon icon={faCalendarDays} className="text-400" transform="shrink-1" />
                      <span>
                        آخرین اجرا:{' '}
                        <span className="fw-medium text-800">
                          {new Date(lastRunAt).toLocaleDateString('fa-IR')}
                        </span>
                      </span>
                    </span>
                  ) : null}
                  {languageLabel ? (
                    <span className="d-inline-flex align-items-center gap-1 fs--1 text-600">
                      <FontAwesomeIcon icon={faLanguage} className="text-400" transform="shrink-1" />
                      <span className="fw-medium text-800">{languageLabel}</span>
                    </span>
                  ) : null}
                </div>
                {display.providers.length > 0 ? (
                  <div className="d-flex align-items-center gap-2 ms-auto">
                    <span className="fs--2 text-500">مدل‌ها</span>
                    <div className="d-flex align-items-center gap-1">
                      {display.providers.map(provider => (
                        <ModelIcon
                          key={provider.modelId}
                          svg={provider.iconSvg}
                          name={provider.publicName}
                          size={22}
                          className="rounded-2 border border-200 bg-white dark__bg-1000"
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </Card.Body>
          </Card>

          {inspectorTab === 'history' ? (
            <PromptHistoryPanel
              promptId={prompt.id}
              refreshKey={comparisonRefreshKey}
              onViewExecution={viewExecution}
            />
          ) : inspectorTab === 'sources' ? (
            <PromptSourcesPanel promptId={prompt.id} rangeQuery={rangeQueryOptions} />
          ) : inspectorTab === 'responses' ? (
            <>
              <ProviderComparison
                promptId={prompt.id}
                refreshKey={comparisonRefreshKey}
                onCreateCompetitor={onCreateCompetitor}
                onAddedCompetitor={() => setComparisonRefreshKey(key => key + 1)}
              />

              {loadingDetail ? (
                <PromptModelResponseSkeleton />
              ) : (
                showModelResponse && (
                  <Card className="overflow-hidden ag-grid-in-card mb-0">
                    <FalconCardHeader
                      light
                      titleTag="h6"
                      className="py-2"
                      title={
                        <span className="d-inline-flex align-items-center gap-2">
                          <IconItem
                            tag="div"
                            icon="comment"
                            bg="soft-success"
                            color="success"
                            size="sm"
                            iconClass="fs--2"
                            className="shadow-none"
                          />
                          <span className="d-inline-flex align-items-center gap-1">
                            پاسخ مدل
                            <HelpTooltip html={getHelpHtml('model_response')} />
                          </span>
                        </span>
                      }
                      endEl={
                        <ModelSwitch
                          items={providerResponses.map(pr => ({
                            id: pr.modelId,
                            name: pr.publicName,
                            iconSvg: pr.iconSvg,
                            status: pr.status,
                          }))}
                          activeId={selectedProviderId}
                          onChange={setSelectedProviderId}
                        />
                      }
                    />
                    <Card.Body className="pt-2 pb-3">
                      {activeExecutedAt ? (
                        <p className="fs--2 text-500 mb-2">{timeAgo(activeExecutedAt)}</p>
                      ) : null}

                      {responseText ? (
                        <AiResponseText
                          text={responseText}
                          promptText={display.text}
                          modelLabel={activeModelLabel}
                          modelIconSvg={activeProvider?.iconSvg ?? null}
                          projectId={projectId}
                          maxHeight="14rem"
                          onCreateCompetitor={onCreateCompetitor}
                        />
                      ) : activeProvider?.status === 'failed' ? (
                        <div className="rounded-3 border border-danger-subtle bg-danger-subtle px-3 py-2 fs--1 text-danger">
                          آخرین اجرای این پرووایدر ناموفق بود. دوباره اجرا کنید.
                        </div>
                      ) : (
                        <div className="rounded-3 border border-dashed bg-body-tertiary px-3 py-3 text-center fs--1 text-700">
                          هنوز پاسخی از این پرووایدر نیست.
                        </div>
                      )}

                      <div className="border-top border-200 mt-3 pt-3">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                          <p className="fs--1 fw-semibold text-700 mb-0">
                            <span className="d-inline-flex align-items-center gap-1">
                              برندهای شناسایی‌شده
                              <HelpTooltip html={getHelpHtml('detected_brands')} />
                            </span>
                          </p>
                          {topDetectedBrands.length > 0 ? (
                            <TopBrandAvatars
                              brands={topDetectedBrands}
                              brandsFound={topDetectedBrands.length}
                              max={5}
                            />
                          ) : null}
                        </div>
                        {activeBrands.length > 0 ? (
                          <BrandsTable
                            brands={activeBrands}
                            projectId={projectId}
                            onAddCompetitor={
                              onAddCompetitor || onCreateCompetitor
                                ? handleBrandsTableAdd
                                : undefined
                            }
                            showHeader={false}
                            showDebugToggle={false}
                            compact
                            rowHeight={32}
                            headerHeight={34}
                          />
                        ) : (
                          <p className="fs--1 text-700 mb-0">برندی برای این مدل شناسایی نشد.</p>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                )
              )}
            </>
          ) : (
            <>
              {duplicateExecution && onViewDuplicateResult && (
                <DuplicateExecutionNotice
                  duplicate={duplicateExecution}
                  onViewResult={() => onViewDuplicateResult(duplicateExecution.existingRequestId)}
                />
              )}

              {(display.status === 'pending' || display.status === 'draft') && !hasResponse ? (
                <div className="rounded-3 border border-dashed bg-body-tertiary p-4 text-center">
                  <p className="fs--1 text-700 mb-0">
                    این پرامپت هنوز اجرا نشده است. از دکمه اجرا استفاده کنید.
                  </p>
                </div>
              ) : (
                <PromptOverviewPanel
                  overview={overview}
                  loading={loadingOverview}
                  error={overviewError}
                  onRetry={() => setComparisonRefreshKey(k => k + 1)}
                />
              )}
            </>
          )}
        </div>
      </DrawerWrapper>
      <CompetitorModal
        open={competitorModalOpen}
        onClose={() => {
          setCompetitorModalOpen(false);
          setCompetitorModalBrand(null);
        }}
        initial={
          competitorModalBrand
            ? {
                brandName: competitorModalBrand.canonicalName ?? competitorModalBrand.brandName,
                websiteUrl: '',
                aliases: [],
              }
            : undefined
        }
        onSubmit={handleCompetitorModalSubmit}
        saving={savingCompetitor}
      />
    </>
  );
}
